import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRedirect } from './sim/authRedirect';
import { postLogin, friendlyLoginError } from '../utils/loginRequest';

function UserLogin({ onLoginSuccess }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use /api/login endpoint (not /api/auth/login)
      const response = await postLogin(
        { email, password, secret: '' },
        () => setRetrying(true),
      );

      if (response.data.success && response.data.token) {
        // Check if user has admin role
        const userRole = response.data.role || 'user';
        
        // Block admin users from logging in via user login page
        if (userRole === 'admin' || userRole === 'owner') {
          setError('Admin accounts cannot login here. Please use the admin login page.');
          setLoading(false);
          return;
        }

        // Store token and user info
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_email', email);
        localStorage.setItem('user_role', userRole);
        
        onLoginSuccess();
        navigate(getRedirect());
      } else {
        setError(response.data.message || 'Login failed');
      }
    } catch (err) {
      setError(friendlyLoginError(err, 'Login failed. Please check your credentials.'));
    } finally {
      setRetrying(false);
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{
        width: '100%',
        maxWidth: '450px',
        background: 'white',
        borderRadius: '20px',
        padding: '3rem',
        boxShadow: '0 10px 40px rgba(139, 92, 246, 0.2)',
        border: '2px solid #e9d5ff'
      }}>
        {/* Logo/Title */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '900',
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem',
            fontFamily: "'Montserrat', sans-serif"
          }}>
            Trade Blindfolded
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
            User Login
          </p>
          {/* Security Message */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: '#f3e8ff',
            borderRadius: '8px',
            border: '1px solid #e9d5ff'
          }}>
            <span style={{ fontSize: '1rem' }}>🔒</span>
            <span style={{ 
              color: '#7c3aed', 
              fontSize: '0.75rem', 
              fontWeight: '600' 
            }}>
              Secure access - Your credentials are protected with encryption
            </span>
          </div>
        </div>

        {/* Error Message */}
        {retrying && !error && (
          <div data-testid="login-retrying-notice" style={{
            padding: '1rem',
            background: '#f3e8ff',
            border: '1px solid #e9d5ff',
            borderRadius: '8px',
            color: '#6b21a8',
            fontSize: '0.875rem',
            marginBottom: '1.5rem'
          }}>
            Connecting to server… retrying
          </div>
        )}
        {error && (
          <div data-testid="login-error-message" style={{
            padding: '1rem',
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b',
            fontSize: '0.875rem',
            marginBottom: '1.5rem'
          }}>
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#374151',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              Email Address
            </label>
            <input
              type="email"
              data-testid="user-login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e9d5ff',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#a855f7'}
              onBlur={(e) => e.target.style.borderColor = '#e9d5ff'}
              placeholder="your@email.com"
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#374151',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              Password
            </label>
            <input
              type="password"
              data-testid="user-login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e9d5ff',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#a855f7'}
              onBlur={(e) => e.target.style.borderColor = '#e9d5ff'}
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            data-testid="user-login-submit-btn"
            disabled={loading}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.3s ease',
              marginBottom: '1rem'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Forgot Password Link */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={() => navigate('/forgot-password')}
            style={{
              background: 'none',
              border: 'none',
              color: '#7c3aed',
              fontSize: '0.875rem',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Forgot Password?
          </button>
        </div>

        {/* Back to Home */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserLogin;
